import { AxiosResponse } from "axios";
import { _get, _post, _aiTraceAsError } from "./BaseApiService";

// Endpoints
const GRID_LAYOUT_API = "User/Preferences";

// A single grid's layout within a page. `state` is the raw DevExtreme
// `dataGrid.state()` object and is treated as an opaque blob so new
// columns/options never break the contract.
export interface GridLayoutEntry {
    gridKey: string;
    viewKey?: string;
    state: object;
}

const DEFAULT_VIEW_KEY = "default";
const LAYOUT_FORMAT = "devextreme-state";
const LAYOUT_CONTENT_VERSION = 1;

/**
 * Service for durable, per-user DevExtreme grid layout persistence.
 *
 * Calls are batched per PAGE (not per grid): one GET loads every grid's layout
 * for a page, and one POST upserts all changed grids for a page at once.
 *
 * Only GET (load) and POST (upsert) are used. The raw DevExtreme grid state is
 * stored as an opaque blob so the frontend has no coupling to column shape.
 *
 * Security note: user identity is derived server-side from the bearer token, so
 * no user identifiers are trusted from the client payload.
 */
class GridLayoutService {
    /**
     * Loads every saved grid layout for a page in a single call.
     * The grids live at `response.data.data.grids` as an array of
     * `{ gridKey, viewKey, layout: { state } }`.
     */
    getPageLayouts(pageKey: string): Promise<AxiosResponse<any>> {
        const params = new URLSearchParams({ pageKey });
        return _get(`${GRID_LAYOUT_API}?${params.toString()}`);
    }

    /**
     * Creates or updates (upsert) multiple grid layouts for a page in a single
     * call. Only the grids that changed should be passed in.
     */
    savePageLayouts(pageKey: string, grids: GridLayoutEntry[]): Promise<AxiosResponse<any>> {
        return _post(`${GRID_LAYOUT_API}`, {
            pageKey,
            grids: grids.map((grid) => ({
                gridKey: grid.gridKey,
                viewKey: grid.viewKey && grid.viewKey.length > 0 ? grid.viewKey : DEFAULT_VIEW_KEY,
                layout: {
                    format: LAYOUT_FORMAT,
                    contentVersion: LAYOUT_CONTENT_VERSION,
                    state: grid.state,
                },
            })),
        });
    }

    traceAsErrorToAppInsights(message: string): boolean {
        return _aiTraceAsError(message);
    }
}

export default GridLayoutService;
